import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import { Plus, Pencil, Trash2, Search } from 'lucide-react';
import api from '@/lib/api';

export default function AdminGyms() {
    const [gyms, setGyms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(1);
    const [meta, setMeta] = useState({});
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [owners, setOwners] = useState([]);
    const [form, setForm] = useState({ name: '', owner_user_id: '', address: '', phone: '', email: '', lat: '', lng: '', status: 'active' });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    const fetchGyms = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get('/admin/gyms', { params: { search, page, per_page: 10 } });
            setGyms(res.data.data);
            setMeta({ last_page: res.data.last_page, current_page: res.data.current_page, total: res.data.total });
        } catch {} finally { setLoading(false); }
    }, [search, page]);

    useEffect(() => { fetchGyms(); }, [fetchGyms]);

    const fetchFormData = async () => {
        const res = await api.get('/admin/owners?per_page=100');
        setOwners(res.data.data);
    };

    const openCreate = async () => {
        setEditing(null);
        setForm({ name: '', owner_user_id: '', address: '', phone: '', email: '', lat: '', lng: '', status: 'active' });
        setError('');
        await fetchFormData();
        setDialogOpen(true);
    };

    const openEdit = async (gym) => {
        setEditing(gym);
        setForm({
            name: gym.name || '',
            owner_user_id: gym.owner_user_id || '',
            address: gym.address || '',
            phone: gym.phone || '',
            email: gym.email || '',
            lat: gym.lat ?? '',
            lng: gym.lng ?? '',
            status: gym.status || 'active',
        });
        setError('');
        await fetchFormData();
        setDialogOpen(true);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        const payload = {
            ...form,
            lat: form.lat ? parseFloat(form.lat) : undefined,
            lng: form.lng ? parseFloat(form.lng) : undefined,
        };
        try {
            if (editing) {
                await api.put(`/admin/gyms/${editing.id}`, payload);
            } else {
                await api.post('/admin/gyms', payload);
            }
            setDialogOpen(false);
            fetchGyms();
        } catch (err) {
            setError(err.response?.data?.message || 'Error al guardar.');
        } finally { setSaving(false); }
    };

    const handleDelete = async (id) => {
        if (!confirm('¿Estás seguro de eliminar este gimnasio?')) return;
        try {
            await api.delete(`/admin/gyms/${id}`);
            fetchGyms();
        } catch {}
    };

    const statusBadge = (status) => {
        const map = { active: 'success', inactive: 'secondary', suspended: 'outline' };
        const labels = { active: 'Activo', inactive: 'Inactivo', suspended: 'Suspendido' };
        return <Badge variant={map[status] || 'secondary'}>{labels[status] || status}</Badge>;
    };

    return (
        <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
                <h1 className="text-2xl font-bold uppercase tracking-wide">Gimnasios</h1>
                <Button onClick={openCreate} className="w-full sm:w-auto"><Plus className="h-4 w-4 mr-2" />Nuevo Gimnasio</Button>
            </div>

            <Card className="bg-[#0a0a0a] border-hairline-dark">
                <CardHeader>
                    <div className="flex items-center gap-2">
                        <Search className="h-4 w-4 text-white/60" />
                        <Input
                            placeholder="Buscar gimnasios..."
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                            className="max-w-sm"
                        />
                    </div>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <p className="text-white/60 text-center py-8">Cargando...</p>
                    ) : gyms.length === 0 ? (
                        <p className="text-white/60 text-center py-8">No se encontraron gimnasios.</p>
                    ) : (
                        <>
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm min-w-[600px]">
                                    <thead>
                                        <tr className="border-b border-hairline-dark">
                                            <th className="text-left py-3 px-2 font-medium">Nombre</th>
                                            <th className="text-left py-3 px-2 font-medium">Propietario</th>
                                            <th className="text-left py-3 px-2 font-medium">Plan</th>
                                            <th className="text-left py-3 px-2 font-medium">Coordenadas</th>
                                            <th className="text-left py-3 px-2 font-medium">Estado</th>
                                            <th className="text-right py-3 px-2 font-medium">Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {gyms.map(gym => (
                                            <tr key={gym.id} className="border-b border-hairline-dark last:border-0 hover:bg-white/5">
                                                <td className="py-3 px-2 font-medium">{gym.name}</td>
                                                <td className="py-3 px-2">{gym.owner?.name || '-'}</td>
                                                <td className="py-3 px-2">{gym.owner?.subscription?.plan?.name || '-'}</td>
                                                <td className="py-3 px-2 font-mono text-xs">
                                                    {gym.lat != null && gym.lng != null
                                                        ? `${Number(gym.lat).toFixed(4)}, ${Number(gym.lng).toFixed(4)}`
                                                        : '-'}
                                                </td>
                                                <td className="py-3 px-2">{statusBadge(gym.status)}</td>
                                                <td className="py-3 px-2 text-right">
                                                    <div className="flex justify-end gap-1">
                                                        <Button variant="outline" size="icon" onClick={() => openEdit(gym)}>
                                                            <Pencil className="h-4 w-4" />
                                                        </Button>
                                                        <Button variant="outline" size="icon" onClick={() => handleDelete(gym.id)}>
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {meta.last_page > 1 && (
                                <div className="flex justify-center gap-2 mt-4">
                                    <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Anterior</Button>
                                    <span className="flex items-center text-sm text-white/60">Página {meta.current_page} de {meta.last_page}</span>
                                    <Button variant="outline" size="sm" disabled={page >= meta.last_page} onClick={() => setPage(p => p + 1)}>Siguiente</Button>
                                </div>
                            )}
                        </>
                    )}
                </CardContent>
            </Card>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="bg-[#0a0a0a] border-hairline-dark">
                    <DialogHeader>
                        <DialogTitle>{editing ? 'Editar Gimnasio' : 'Nuevo Gimnasio'}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSave} className="space-y-4">
                        {error && <div className="bg-[#0a0a0a] border border-hairline-dark text-white text-sm p-3 rounded-md">{error}</div>}
                        <div className="space-y-2">
                            <Label>Nombre</Label>
                            <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
                        </div>
                        <div className="space-y-2">
                            <Label>Propietario</Label>
                            <select
                                className="flex h-9 w-full rounded-md border border-hairline-dark bg-[#0a0a0a] px-3 py-1 text-sm text-white"
                                value={form.owner_user_id}
                                onChange={e => setForm({ ...form, owner_user_id: e.target.value })}
                                required
                            >
                                <option value="" className="bg-[#0a0a0a] text-white">Seleccionar...</option>
                                {owners.map(o => <option key={o.id} value={o.id} className="bg-[#0a0a0a] text-white">{o.name} ({o.email})</option>)}
                            </select>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Dirección</Label>
                                <Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} />
                            </div>
                            <div className="space-y-2">
                                <Label>Teléfono</Label>
                                <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
                            </div>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Email</Label>
                                <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
                            </div>
                            <div className="space-y-2">
                                <Label>Latitud</Label>
                                <Input value={form.lat} onChange={e => setForm({ ...form, lat: e.target.value })} placeholder="-34.6037" />
                            </div>
                            <div className="space-y-2">
                                <Label>Longitud</Label>
                                <Input value={form.lng} onChange={e => setForm({ ...form, lng: e.target.value })} placeholder="-58.3816" />
                            </div>
                            <div className="space-y-2">
                                <Label>Estado</Label>
                                <select
                                    className="flex h-9 w-full rounded-md border border-hairline-dark bg-[#0a0a0a] px-3 py-1 text-sm text-white"
                                    value={form.status}
                                    onChange={e => setForm({ ...form, status: e.target.value })}
                                >
                                    <option value="active" className="bg-[#0a0a0a] text-white">Activo</option>
                                    <option value="inactive" className="bg-[#0a0a0a] text-white">Inactivo</option>
                                    <option value="suspended" className="bg-[#0a0a0a] text-white">Suspendido</option>
                                </select>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                            <Button type="submit" disabled={saving}>{saving ? 'Guardando...' : 'Guardar'}</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}
